import { useParams, Link } from 'react-router-dom';
import { mangaList, categories } from '@/mocks/manga';
import MangaCard from '@/components/base/MangaCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams();
  const manga = mangaList.find(m => m.id === id);

  if (!manga) {
    return (
      <main className="pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-4">该漫画可能已被移除或链接无效</p>
          <Link to="/" className="inline-flex items-center gap-1 px-5 py-2 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const catInfo = categories.find(c => c.slug === manga.category);
  const categoryName = catInfo ? catInfo.name : manga.category;
  const relatedManga = mangaList.filter(m => m.category === manga.category && m.id !== manga.id).slice(0, 4);

  return (
    <main className="pt-16">
      <div className="mx-auto px-4 md:px-6 max-w-[1280px]">
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: `${categoryName}漫画`, href: `/category/${manga.category}` },
          { label: manga.title },
        ]} />

        {/* Manga Detail */}
        <article className="flex flex-col md:flex-row gap-6 md:gap-8 py-6">
          {/* Cover */}
          <div className="flex-shrink-0 w-full md:w-[280px]">
            <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
              <img
                src={manga.coverImage}
                alt={manga.title}
                title={`${manga.title} - 韩漫在线观看`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              {manga.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 mt-3">
              <span className="flex items-center gap-1 text-sm">
                <i className="ri-user-line text-foreground-400"></i>
                <span className="text-foreground-600">作者：{manga.author}</span>
              </span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${manga.status === '连载中' ? 'bg-accent-100 text-accent-800' : 'bg-secondary-200 text-secondary-800'}`}>
                {manga.status}
              </span>
              <span className="flex items-center gap-1 text-sm text-foreground-600">
                <i className="ri-star-fill text-accent-500"></i>
                {manga.rating}
              </span>
              <span className="flex items-center gap-1 text-sm text-foreground-600">
                <i className="ri-eye-line text-foreground-400"></i>
                {(manga.views / 10000).toFixed(1)}万
              </span>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {manga.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-background-100 text-xs text-foreground-600 rounded-full">{tag}</span>
              ))}
            </div>

            {/* Description */}
            <div className="mt-5">
              <h2 className="text-base font-semibold text-foreground-900 mb-2">作品简介</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{manga.description}</p>
            </div>

            {/* Read Button */}
            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <button className="w-full sm:w-auto px-8 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold rounded-full hover:bg-primary-600 transition-colors text-base whitespace-nowrap cursor-pointer">
                开始阅读 第1话
              </button>
              <button className="w-full sm:w-auto px-6 py-3 bg-background-100 text-foreground-700 font-medium rounded-full hover:bg-background-200 transition-colors text-sm whitespace-nowrap cursor-pointer">
                <i className="ri-bookmark-line mr-1"></i>加入收藏
              </button>
            </div>
          </div>
        </article>

        {/* Chapter List */}
        <section className="py-8">
          <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
            章节列表 <span className="text-sm font-normal text-foreground-500 ml-2">共 {manga.chapters.length} 话</span>
          </h2>
          <div className="border border-background-200/70 rounded-lg overflow-hidden">
            <div className="divide-y divide-background-200/70 max-h-[500px] overflow-y-auto">
              {manga.chapters.map((chapter) => (
                <div key={chapter.number} className="flex items-center justify-between px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-foreground-500 font-medium bg-background-100 px-2 py-0.5 rounded">第{chapter.number}话</span>
                    <span className="text-sm text-foreground-800 font-medium">{chapter.title}</span>
                  </div>
                  <span className="text-xs text-foreground-400">{chapter.date}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Manga */}
        {relatedManga.length > 0 && (
          <section className="py-8 border-t border-background-200/70">
            <h2 className="text-lg font-bold text-foreground-950 mb-5" style={{ fontFamily: 'var(--font-heading)' }}>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {relatedManga.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          </section>
        )}

        {/* SEO Structured Data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `${manga.title} - 韩漫在线观看`,
          "description": manga.description,
          "author": { "@type": "Person", "name": manga.author },
          "datePublished": manga.chapters[0]?.date || manga.updateDate,
          "dateModified": manga.updateDate,
          "image": manga.coverImage,
          "publisher": { "@type": "Organization", "name": "韩漫之家" },
          "mainEntityOfPage": { "@type": "WebPage", "@id": `https://www.hanmanzhijia.com/article/${manga.id}` }
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.hanmanzhijia.com" },
            { "@type": "ListItem", "position": 2, "name": `${categoryName}漫画`, "item": `https://www.hanmanzhijia.com/category/${manga.category}` },
            { "@type": "ListItem", "position": 3, "name": manga.title }
          ]
        }) }} />
      </div>
    </main>
  );
}